/**
 * Kaseya API Client Service
 * Wraps calls to the kaseya-api Edge Function
 */

import { supabase } from '../lib/supabase'

export interface KaseyaOrganization {
  Id: number
  Name: string
  HasCustomFields: boolean
  PsaMappingId: number | null
  PsaMappingType: string | null
  Access: Array<{
    TeamName: string
    AccessLevel: string
  }>
}

export interface KaseyaGroup {
  Id: number
  Name: string
  ParentSiteId: number
  ParentSiteName: string
  ParentOrganizationId: number
  ParentOrganizationName: string
  Notes: string | null
  HasCustomFields: boolean
  PsaMappingId: number | null
  PsaMappingType: string | null
}

export interface KaseyaDevice {
  Identifier: string
  Name: string
  GroupId: number
  GroupName: string
  IsAgentInstalled: boolean
  IsMdmEnrolled: boolean
  SiteId: number
  SiteName: string
  OrganizationId: number
  OrganizationName: string
  HasCustomFields: boolean
}

export interface KaseyaAsset {
  Identifier: string
  Name: string
  GroupName: string
  Tags?: string[]
  AssetInfo?: Record<string, any>
  Security?: {
    Antivirus?: Record<string, any>
    Firewall?: Record<string, any>
    UAC?: Record<string, any>
    RansomwareDetection?: Record<string, any>
  }
  IpAddresses?: Array<{
    IpAddress: string
    MacAddress: string
    NetworkAdapter: string
  }>
  LocalIpAddresses?: string[]
  Disks?: Array<{
    Name: string
    TotalSpace: number
    FreeSpace: number
    UsedSpace: number
  }>
  InstalledSoftware?: Array<{
    Name: string
    Version: string
    Publisher: string
  }>
  Updates?: {
    Critical: number
    Important: number
    Moderate: number
    Low: number
  }
  AvailableUpdates?: Array<{
    Title: string
    Severity: string
    KbArticleId: string
  }>
}

interface KaseyaResponse<T> {
  Data: T
  Meta: {
    ResponseCode: number
    TotalCount?: number
    NextQueryLink?: string
  }
}

class KaseyaAPIService {
  /**
   * Get all Kaseya organizations
   */
  async getOrganizations(): Promise<KaseyaOrganization[]> {
    const { data: { session } } = await supabase.auth.getSession()

    // Call without query params - Kaseya returns all orgs by default
    const path = 'kaseya-api/organizations'
    console.log('Fetching organizations from:', path)

    const { data, error } = await supabase.functions.invoke(path, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to fetch organizations:', error)
      console.error('Response data:', data)
      throw new Error(error.message || 'Failed to fetch organizations')
    }

    // Check if response contains error from Kaseya
    if (data?.error) {
      console.error('Kaseya API error:', data)
      throw new Error(data.error || 'Kaseya API error')
    }

    const response = data as KaseyaResponse<KaseyaOrganization[]>
    console.log(`✅ Fetched ${response.Data?.length || 0} organizations from Kaseya`)
    return response.Data || []
  }

  /**
   * Get all Kaseya machine groups
   */
  async getGroups(organizationId?: number): Promise<KaseyaGroup[]> {
    const { data: { session } } = await supabase.auth.getSession()

    // Call without query params for now - will add filtering later if needed
    const path = 'kaseya-api/groups'
    console.log('Fetching groups from:', path)

    const { data, error } = await supabase.functions.invoke(path, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to fetch groups:', error)
      console.error('Response data:', data)
      throw new Error(error.message || 'Failed to fetch groups')
    }

    // Check if response contains error from Kaseya
    if (data?.error) {
      console.error('Kaseya API error:', data)
      throw new Error(data.error || 'Kaseya API error')
    }

    const response = data as KaseyaResponse<KaseyaGroup[]>
    let groups = response.Data || []

    // Filter by organization if specified (client-side filtering)
    if (organizationId) {
      groups = groups.filter(g => g.ParentOrganizationId === organizationId)
    }

    console.log(`✅ Fetched ${groups.length} groups from Kaseya${organizationId ? ` (filtered by org ${organizationId})` : ''}`)
    return groups
  }

  /**
   * Get a specific Kaseya group
   */
  async getGroup(groupId: number): Promise<KaseyaGroup> {
    const { data: { session } } = await supabase.auth.getSession()

    const { data, error } = await supabase.functions.invoke(`kaseya-api/groups/${groupId}`, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error(`Failed to fetch group ${groupId}:`, error)
      throw new Error(error.message || 'Failed to fetch group')
    }

    const response = data as KaseyaResponse<KaseyaGroup>
    return response.Data
  }

  /**
   * Get all devices from Kaseya
   */
  async getDevices(groupId?: number): Promise<KaseyaDevice[]> {
    const { data: { session } } = await supabase.auth.getSession()
    let path = 'kaseya-api/devices'

    if (groupId) {
      path += `?$filter=GroupId eq ${groupId}`
    }

    const { data, error } = await supabase.functions.invoke(path, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to fetch devices:', error)
      throw new Error(error.message || 'Failed to fetch devices')
    }

    const response = data as KaseyaResponse<KaseyaDevice[]>
    return response.Data || []
  }

  /**
   * Get detailed asset information for a specific device
   */
  async getAsset(deviceId: string, include: string = 'All'): Promise<KaseyaAsset> {
    const { data: { session } } = await supabase.auth.getSession()
    const path = `kaseya-api/assets/${deviceId}?include=${include}`

    const { data, error } = await supabase.functions.invoke(path, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error(`Failed to fetch asset ${deviceId}:`, error)
      throw new Error(error.message || 'Failed to fetch asset')
    }

    const response = data as KaseyaResponse<KaseyaAsset>
    return response.Data
  }

  /**
   * Get assets for multiple devices
   */
  async getAssets(groupId?: number, include: string = 'All'): Promise<KaseyaAsset[]> {
    const { data: { session } } = await supabase.auth.getSession()
    let path = `kaseya-api/assets?include=${include}`

    if (groupId) {
      path += `&$filter=GroupId eq ${groupId}`
    }

    const { data, error } = await supabase.functions.invoke(path, {
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to fetch assets:', error)
      throw new Error(error.message || 'Failed to fetch assets')
    }

    const response = data as KaseyaResponse<KaseyaAsset[]>
    return response.Data || []
  }

  // ============================================================================
  // WRITE OPERATIONS (Bidirectional Sync)
  // ============================================================================

  /**
   * Create a new organization in Kaseya
   */
  async createOrganization(name: string): Promise<{ Id: number; Name: string }> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Creating organization in Kaseya: ${name}`)

    const { data, error } = await supabase.functions.invoke('kaseya-api/organizations', {
      body: { Name: name },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to create organization:', error)
      throw new Error(error.message || 'Failed to create organization in Kaseya')
    }

    console.log('Raw Kaseya API response:', data)

    const response = data as KaseyaResponse<{ Id: number; Name: string }>

    if (!response.Data || !response.Data.Id) {
      console.error('Invalid response from Kaseya API:', response)
      throw new Error('Kaseya API did not return organization ID')
    }

    console.log(`✅ Created organization in Kaseya: ID ${response.Data.Id}`)
    return response.Data
  }

  /**
   * Update an existing organization in Kaseya
   */
  async updateOrganization(organizationId: number, name: string): Promise<{ Id: number; Name: string }> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Updating organization ${organizationId} in Kaseya: ${name}`)

    const { data, error } = await supabase.functions.invoke(`kaseya-api/organizations/${organizationId}`, {
      body: { Name: name },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to update organization:', error)
      throw new Error(error.message || 'Failed to update organization in Kaseya')
    }

    const response = data as KaseyaResponse<{ Id: number; Name: string }>
    console.log(`✅ Updated organization in Kaseya: ID ${response.Data.Id}`)
    return response.Data
  }

  /**
   * Create a new site in Kaseya (required for group hierarchy)
   */
  async createSite(data: {
    Name: string;
    ParentId: number;
    ContactInformation?: Record<string, any>
  }): Promise<{ Id: number; Name: string; ParentOrganizationId: number }> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Creating site in Kaseya: ${data.Name} under org ${data.ParentId}`)

    const { data: responseData, error } = await supabase.functions.invoke('kaseya-api/sites', {
      body: data,
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to create site:', error)
      throw new Error(error.message || 'Failed to create site in Kaseya')
    }

    const response = responseData as KaseyaResponse<{ Id: number; Name: string; ParentOrganizationId: number }>
    console.log(`✅ Created site in Kaseya: ID ${response.Data.Id}`)
    return response.Data
  }

  /**
   * Create a new machine group in Kaseya
   */
  async createGroup(data: {
    Name: string;
    ParentId: number;
    Notes?: string
  }): Promise<KaseyaGroup> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Creating machine group in Kaseya: ${data.Name} under site ${data.ParentId}`)

    const { data: responseData, error } = await supabase.functions.invoke('kaseya-api/groups', {
      body: data,
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to create group:', error)
      throw new Error(error.message || 'Failed to create group in Kaseya')
    }

    const response = responseData as KaseyaResponse<KaseyaGroup>
    console.log(`✅ Created group in Kaseya: ID ${response.Data.Id}`)
    return response.Data
  }

  /**
   * Update an existing machine group in Kaseya
   */
  async updateGroup(groupId: number, data: { Name: string; Notes?: string }): Promise<KaseyaGroup> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Updating group ${groupId} in Kaseya: ${data.Name}`)

    const { data: responseData, error } = await supabase.functions.invoke(`kaseya-api/groups/${groupId}`, {
      body: data,
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to update group:', error)
      throw new Error(error.message || 'Failed to update group in Kaseya')
    }

    const response = responseData as KaseyaResponse<KaseyaGroup>
    console.log(`✅ Updated group in Kaseya: ID ${response.Data.Id}`)
    return response.Data
  }

  /**
   * Publish/register a new device in Kaseya
   */
  async publishDevice(data: {
    InstanceId: string;
    GroupId: number;
    Name?: string;
    Description?: string;
  }): Promise<KaseyaDevice> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Publishing device to Kaseya: ${data.Name || data.InstanceId} in group ${data.GroupId}`)

    const { data: responseData, error } = await supabase.functions.invoke('kaseya-api/devices', {
      body: data,
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to publish device:', error)
      throw new Error(error.message || 'Failed to publish device in Kaseya')
    }

    const response = responseData as KaseyaResponse<KaseyaDevice>
    console.log(`✅ Published device in Kaseya: ${response.Data.Identifier}`)
    return response.Data
  }

  /**
   * Move a device to a different machine group in Kaseya
   */
  async moveDevice(deviceId: string, groupId: number): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Moving device ${deviceId} to group ${groupId} in Kaseya`)

    const { error } = await supabase.functions.invoke(`kaseya-api/devices/${deviceId}/move`, {
      body: { GroupId: groupId },
      headers: {
        Authorization: `Bearer ${session?.access_token}`
      }
    })

    if (error) {
      console.error('Failed to move device:', error)
      throw new Error(error.message || 'Failed to move device in Kaseya')
    }

    console.log(`✅ Moved device ${deviceId} to group ${groupId} in Kaseya`)
  }

  /**
   * Get installer package URL for a specific machine group
   * Used to get group-specific installer URLs with secrets
   */
  async getGroupPackage(groupId: number, packageType: string): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Fetching installer package for group ${groupId}, type: ${packageType}`)

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kaseya-api/groups/${groupId}/package/${packageType}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch package' }))
      console.error('Failed to fetch group package:', errorData)
      throw new Error(errorData.error || 'Failed to fetch installer package from Kaseya')
    }

    // The response should contain the installer URL
    const data = await response.json() as KaseyaResponse<{ Url: string; Name: string }>
    console.log(`✅ Got installer package URL for ${packageType}:`, data.Data.Url)
    return data.Data.Url
  }

  /**
   * Get all available installer packages for a group
   */
  async getGroupPackages(groupId: number): Promise<Array<{ Type: string; Name: string; Url?: string }>> {
    console.log(`Fetching all installer packages for group ${groupId}`)

    const { data, error } = await supabase.functions.invoke(`kaseya-api/groups/${groupId}`)

    if (error) {
      console.error('Failed to fetch group packages:', error)
      throw new Error(error.message || 'Failed to fetch group packages from Kaseya')
    }

    const response = data as KaseyaResponse<{
      Id: number
      Name: string
      Packages: Array<{ Type: string; Name: string }>
    }>

    console.log(`✅ Got ${response.Data.Packages?.length || 0} packages for group ${groupId}`)
    return response.Data.Packages || []
  }

  // ============================================================================
  // NEW: DEVICE DEPLOYMENT SUPPORT
  // ============================================================================

  /**
   * Static URLs for macOS and Linux installers
   * Windows uses dynamic Kaseya API URLs
   */
  private static STATIC_AGENT_URLS: Record<string, string> = {
    'macos_x64': 'https://vsax.net/updates.vsax.net/10.21.0/4562D2/VSAX.dmg',
    'macos_arm64': 'https://vsax.net/updates.vsax.net/10.21.0/4562D2/VSAX.pkg',
    'linux_x64': 'https://vsax.net/updates.vsax.net/10.21.0/4562D2/vsax_x64.deb',
    'linux_arm64': 'https://vsax.net/updates.vsax.net/10.21.0/4562D2/vsax_arm64.deb'
  }

  /**
   * Package type mapping: OS + Architecture → Kaseya package type
   */
  private static PACKAGE_TYPE_MAP: Record<string, Record<string, string>> = {
    'windows': {
      'x64': 'windows_agent_x64',
      'x86': 'windows_agent_x86',
      'arm64': 'windows_agent_arm64'
    },
    'macos': {
      'x64': 'macos_agent_x64',
      'arm64': 'macos_agent_arm64'
    },
    'linux': {
      'x64': 'linux_agent_x64',
      'arm64': 'linux_agent_arm64'
    }
  }

  /**
   * Get Kaseya groups for a specific organization
   */
  async getOrganizationGroups(organizationId: number): Promise<KaseyaGroup[]> {
    console.log(`Fetching groups for organization ${organizationId}`)

    // Reuse existing getGroups method with organization filter
    return await this.getGroups(organizationId)
  }

  /**
   * Get installer package URL with OS type and architecture support
   * Windows: Calls Kaseya API for dynamic URLs
   * macOS/Linux: Returns static URLs
   */
  async getGroupPackageWithArch(
    groupId: number,
    osType: string,
    architecture: string
  ): Promise<string> {
    console.log(`Fetching installer for group ${groupId}, OS: ${osType}, arch: ${architecture}`)

    // For Windows, use Kaseya API
    if (osType === 'windows') {
      const packageType = KaseyaAPIService.PACKAGE_TYPE_MAP['windows'][architecture]

      if (!packageType) {
        throw new Error(`Unsupported Windows architecture: ${architecture}`)
      }

      return await this.getGroupPackage(groupId, packageType)
    }

    // For macOS and Linux, use static URLs
    const key = `${osType}_${architecture}`
    const url = KaseyaAPIService.STATIC_AGENT_URLS[key]

    if (!url) {
      throw new Error(`Unsupported OS/architecture combination: ${osType}/${architecture}`)
    }

    console.log(`✅ Using static installer URL for ${osType} ${architecture}`)
    return url
  }

  // ============================================================================
  // DELETE OPERATIONS (Bidirectional Sync)
  // ============================================================================

  /**
   * Delete an organization from Kaseya
   */
  async deleteOrganization(organizationId: number): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Deleting organization ${organizationId} from Kaseya`)

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kaseya-api/organizations/${organizationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Delete failed' }))
      console.error('Failed to delete organization from Kaseya:', errorData)
      throw new Error(errorData.error || 'Failed to delete organization from Kaseya')
    }

    console.log(`✅ Deleted organization ${organizationId} from Kaseya`)
  }

  /**
   * Delete a site from Kaseya
   */
  async deleteSite(siteId: number): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Deleting site ${siteId} from Kaseya`)

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kaseya-api/sites/${siteId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Delete failed' }))
      console.error('Failed to delete site from Kaseya:', errorData)
      throw new Error(errorData.error || 'Failed to delete site from Kaseya')
    }

    console.log(`✅ Deleted site ${siteId} from Kaseya`)
  }

  /**
   * Delete a machine group from Kaseya
   */
  async deleteGroup(groupId: number): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession()

    console.log(`Deleting group ${groupId} from Kaseya`)

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kaseya-api/groups/${groupId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Delete failed' }))
      console.error('Failed to delete group from Kaseya:', errorData)
      throw new Error(errorData.error || 'Failed to delete group from Kaseya')
    }

    console.log(`✅ Deleted group ${groupId} from Kaseya`)
  }
}

// Export singleton instance
export const kaseyaAPI = new KaseyaAPIService()
